<template>
  <div class="content">
    <div class="location">
      <e-breadcrumbs />
    </div>
    <h1 class="page_title">{{ $t('menu.MENU09.020') }}</h1> <!-- Network -->
    <div class="content_box mt10">
      <!-- 표기 언어 토글: '맨위로' 버튼처럼 화면 우하단에 항상 고정 (오른쪽은 콘텐츠 우측선에 정렬) -->
      <div ref="langAnchor"></div>
      <div class="nw-lang-float" :style="{ right: langRight + 'px' }">
        <div class="nw-lang-tg" role="group" aria-label="상세 표기 언어 선택">
          <button type="button" :class="{ on: dispLang === 'loc' }" :aria-pressed="dispLang === 'loc' ? 'true' : 'false'" @click="dispLang = 'loc'">LOCAL</button>
          <button type="button" :class="{ on: dispLang === 'eng' }" :aria-pressed="dispLang === 'eng' ? 'true' : 'false'" @click="dispLang = 'eng'">ENG</button>
        </div>
      </div>
      <div class="network_menu">
        <!-- 권역 그룹핑: 권역 라벨(호버=소속국 일괄강조, 클릭=권역 이동) + 소속 국가 칩. 국가가 적은 권역은 한 줄에 병렬 배치 -->
        <div v-for="(row, ri) in regionRows" :key="'rgrow' + ri" class="rg-row">
          <template v-for="rg in row">
            <a :key="rg.name" href="#" class="rg-label" @mouseenter="hoverRegion(rg)" @mouseleave="hoverRegion(null)" @focus="hoverRegion(rg)" @blur="hoverRegion(null)" @click.prevent="flyRegion(rg)">{{ rg.name }}</a>
            <ul :key="rg.name + '-ul'">
              <li v-for="item in rg.items" :key="item.seq" :id="item.seq" class="list_area" :class="{ on: ctrFlag === item.seq }" @mouseenter="hoverCtr = item.seq" @mouseleave="hoverCtr = ''">
                <a href="#" :aria-current="ctrFlag === item.seq ? 'true' : null" @focus="hoverCtr = item.seq" @blur="hoverCtr = ''" @click.prevent="[getNetworkInfo(item.seq),btnChgImg(item.seq)]">{{ item.name }}</a>
              </li>
            </ul>
          </template>
        </div>
      </div>
      <div class="map_wrap network_map_wrap mt10">
        <!-- 정적 지도 이미지 → 인터랙티브 지도(d3-geo, 외부 CDN 미사용)로 교체 -->
        <network-map ref="nwmap" :seq="ctrFlag" :cities="mapCities" :hover-seq="hoverCtr" :hover-seqs="hoverCtrs" @select="[getNetworkInfo($event), btnChgImg($event)]" @hover="hoverCtr = $event" @city="scrollToCity" />
      </div>
      <!-- 연락처: DB(WB320I→WB321I→WB322I) API 기반. 관리자(ADMIN JWT)는 계층 편집 뷰 + 모달 CRUD -->
      <div v-if="isAdmin" class="nw-adm-bar">
        <button type="button" class="nw-ebtn add big" @click="openOfc(null)">+ 사무소 추가 (현재 국가 아래)</button>
      </div>
      <!-- 포트 포커스 필터: D/O 팝업 등에서 portCd로 진입 시 매칭 사무소만 표시 -->
      <div v-if="portFilterOfcCds.length" class="nw-port-filter">
        <span v-if="serviceLang === 'KOR'"><b>{{ portFilterNm }}</b> 담당 사무소만 표시 중 ({{ officeList.length }}건)</span>
        <span v-else>Showing <b>{{ portFilterNm }}</b> office(s) only ({{ officeList.length }})</span>
        <button type="button" @click="clearPortFilter">{{ serviceLang === 'KOR' ? '전체 보기' : 'Show all' }}</button>
      </div>
      <div v-if="officeList.length" class="nw-offices">
        <div v-for="(ofc, oi) in officeList" :key="ofc.ofcCd" class="nw-office" :class="{ 'nw-off': isAdmin && ofc.useYn === 'N' }" :data-ofc-cd="ofc.ofcCd">
          <div class="nw-office-h">
            <span v-if="isAdmin" class="nw-lvl l1">① 사무소</span>
            <span class="nw-badge" :class="ofc.type === 'AGENCY' ? 'ag' : 'km'">{{ ofc.type }}</span>
            <span v-if="isAdmin && ofc.useYn === 'N'" class="nw-badge off">미사용</span>
            <b>{{ locName(ofc.nm, ofc.enm) }}</b>
            <span v-if="dispLang === 'loc' && ofc.enm && ofc.enm !== ofc.nm" class="nw-en">{{ ofc.enm }}</span>
            <span v-if="isAdmin" class="nw-h-btns">
              <button type="button" class="nw-ebtn ordb" title="위로" :disabled="mSaving || oi === 0" @click="moveOfc(oi, -1)">▲</button>
              <button type="button" class="nw-ebtn ordb" title="아래로" :disabled="mSaving || oi === officeList.length - 1" @click="moveOfc(oi, 1)">▼</button>
              <button type="button" class="nw-ebtn" @click="openOfc(ofc)">✎ 사무소 수정</button>
            </span>
          </div>
          <div v-if="locName(ofc.addr, ofc.addrEnm)" class="nw-office-addr">{{ locName(ofc.addr, ofc.addrEnm) }}<span v-if="ofc.tel"> · TEL {{ ofc.tel }}</span></div>

          <!-- 일반 사용자: 공개 표 -->
          <table v-if="!isAdmin" class="tbl_col nw-pic">
            <caption class="sr-only">{{ locName(ofc.nm, ofc.enm) || ofc.area }} 담당자 연락처</caption>
            <colgroup>
              <col width="18%">
              <col width="24%">
              <col width="26%">
              <col width="16%">
              <col width="16%">
            </colgroup>
            <thead>
              <tr><th scope="col">AREA</th><th scope="col">TEAM / NAME</th><th scope="col">JOB</th><th scope="col">OFFICE TEL.</th><th scope="col">EMAIL</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in officeRows(ofc)" :key="row.key">
                <td v-if="row.first" :rowspan="row.areaSpan" class="nw-area-c" :data-area-cd="row.areaCd">
                  <button type="button" class="nw-area-link" @click="openMap(ofc)">
                    {{ row.area }} <span class="nw-pin" aria-hidden="true"></span><span class="sr-only">지도에서 보기</span>
                  </button>
                </td>
                <td :class="{ 'nw-c': !row.team }">{{ row.team || '-' }}</td>
                <td :class="{ 'nw-c': !row.job }">{{ row.job || '-' }}</td>
                <td :class="{ 'nw-c': !row.tel }">{{ row.tel || '-' }}</td>
                <td :class="{ 'nw-c': !row.email }">
                  <button v-if="row.email" type="button" class="nw-email-copy" title="클릭하여 이메일 주소 복사" @click="copyEmail(row.email, row.key)">
                    {{ row.email }}<span v-if="copiedKey === row.key" class="nw-copied">{{ serviceLang === 'KOR' ? '복사됨' : 'Copied' }}</span>
                  </button>
                  <span v-else>-</span>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- 관리자: 계층 편집 뷰 (①사무소 → ②AREA → ③담당자) -->
          <div v-else class="nw-tree">
            <div v-for="(ar, ai) in (ofc.areas || [])" :key="ar.areaCd" class="nw-node-area" :class="{ 'nw-off': ar.useYn === 'N' }" :data-area-cd="ar.areaCd">
              <div class="nw-node-h">
                <span class="nw-lvl l2">② AREA</span>
                <b>{{ locName(ar.nm, ar.enm) }}</b>
                <span v-if="ar.useYn === 'N'" class="nw-badge off">미사용</span>
                <span class="nw-cd">{{ ar.areaCd }}</span>
                <span class="nw-h-btns">
                  <button type="button" class="nw-ebtn ordb" title="위로" :disabled="mSaving || ai === 0" @click="moveArea(ofc, ai, -1)">▲</button>
                  <button type="button" class="nw-ebtn ordb" title="아래로" :disabled="mSaving || ai === (ofc.areas || []).length - 1" @click="moveArea(ofc, ai, 1)">▼</button>
                  <button type="button" class="nw-ebtn" @click="openArea(ofc, ar)">✎ 수정</button>
                  <button type="button" class="nw-ebtn add" @click="openPic(ofc, ar, null)">+ 담당자 추가 (이 AREA에)</button>
                </span>
              </div>
              <table v-if="(ar.pics || []).length" class="nw-pt">
                <thead>
                  <tr><th scope="col" style="width:96px;">코드</th><th scope="col">팀</th><th scope="col">JOB</th><th scope="col" style="width:130px;">전화</th><th scope="col" style="width:140px;">이메일</th><th scope="col" style="width:80px;"></th></tr>
                </thead>
                <tbody>
                  <tr v-for="(pic, pi) in ar.pics" :key="pic.picCd" :class="{ 'nw-off': pic.useYn === 'N' }">
                    <td class="nw-cd">{{ pic.picCd }} <span v-if="pic.useYn === 'N'" class="nw-badge off">미사용</span></td>
                    <td><span class="nw-lvl l3">③</span> {{ locName(pic.team, pic.teamEnm) }}</td>
                    <td>{{ locName(pic.job, pic.jobEnm) }}</td>
                    <td>{{ pic.tel }}</td>
                    <td>{{ pic.email || '-' }}</td>
                    <td class="nw-r">
                      <button type="button" class="nw-ebtn ordb" title="위로" :disabled="mSaving || pi === 0" @click="movePic(ar, pi, -1)">▲</button>
                      <button type="button" class="nw-ebtn ordb" title="아래로" :disabled="mSaving || pi === ar.pics.length - 1" @click="movePic(ar, pi, 1)">▼</button>
                      <button type="button" class="nw-ebtn" @click="openPic(ofc, ar, pic)">✎ 수정</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="nw-nopic">담당자 없음 — 위 [+ 담당자 추가]로 등록</div>
            </div>
            <div class="nw-addline">
              <button type="button" class="nw-ebtn add" @click="openArea(ofc, null)">+ AREA 추가 (이 사무소 아래)</button>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="nw-empty">등록된 사무소 정보가 없습니다.</div>

      <!-- 관리자 편집 모달 -->
      <div v-if="mType" class="nw-mbg" @click="closeM"></div>
      <div v-if="mType" class="nw-modal">
        <div class="nw-m-h">{{ mTitle }}<button type="button" class="nw-m-x" @click="closeM">✕</button></div>
        <!-- eslint-disable-next-line vue/no-v-html : nwPath()가 모든 값을 이스케이프(내부 데이터 전용) -->
        <div class="nw-m-path" v-html="mPath"></div>
        <div class="nw-m-b">
          <!-- 사무소 폼 -->
          <div v-if="mType === 'ofc'" class="nw-frm">
            <label>구분</label>
            <select v-model="mForm.cat"><option>KMTC</option><option>AGENCY</option></select>
            <label>코드</label><input type="text" :value="mIsNew ? '(자동채번)' : mForm.ofcCd" disabled>
            <!-- 현지어/영문 병행 입력 — 표시는 페이지 LOCAL/ENG 토글 기준(상호 폴백) -->
            <label>사무소명 (현지어)</label><input type="text" v-model="mForm.nm" class="full" placeholder="예: 서울본사">
            <label>사무소명 (영문)</label><input type="text" v-model="mForm.enm" class="full" placeholder="예: KMTC Singapore Office">
            <label>도시(AREA명)</label><input type="text" v-model="mForm.areaNm">
            <label>전화번호</label><input type="text" v-model="mForm.tel">
            <label>주소 (현지어)</label><input type="text" v-model="mForm.addr" class="full">
            <label>주소 (영문)</label><input type="text" v-model="mForm.addrEnm" class="full">
            <label>위도</label><input type="text" v-model="mForm.lat">
            <label>경도</label><input type="text" v-model="mForm.lng">
            <label>지도 URL</label><input type="text" v-model="mForm.mapUrl" class="full" placeholder="구글맵 공유링크 — 비우면 주소로 지도 검색">
            <label>옵션</label>
            <span class="chk full">
              <input type="checkbox" id="nwMapYn" v-model="mForm.mapYnB"><label for="nwMapYn">지도에 표시</label>
              <input type="checkbox" id="nwOUseYn" v-model="mForm.useYnB"><label for="nwOUseYn">사용</label>
            </span>
          </div>
          <!-- AREA 폼 -->
          <div v-if="mType === 'area'" class="nw-frm">
            <label>코드</label><input type="text" :value="mIsNew ? '(자동채번)' : mForm.areaCd" disabled>
            <span></span><span></span>
            <label>AREA명 (현지어)</label><input type="text" v-model="mForm.nm" class="full" placeholder="예: 자카르타">
            <label>AREA명 (영문)</label><input type="text" v-model="mForm.enm" class="full" placeholder="예: JAKARTA">
            <label>옵션</label>
            <span class="chk full"><input type="checkbox" id="nwAUseYn" v-model="mForm.useYnB"><label for="nwAUseYn">사용</label></span>
          </div>
          <!-- 담당자 폼 -->
          <div v-if="mType === 'pic'" class="nw-frm">
            <label>코드</label><input type="text" :value="mIsNew ? '(자동채번)' : mForm.picCd" disabled>
            <span></span><span></span>
            <label>팀명 (현지어)</label><input type="text" v-model="mForm.team" class="full" placeholder="예: 수출영업팀">
            <label>팀명 (영문)</label><input type="text" v-model="mForm.teamEnm" class="full" placeholder="예: Export Sales Team">
            <label>JOB (현지어)</label><input type="text" v-model="mForm.job" class="full" placeholder="예: 부킹, B/L 발행">
            <label>JOB (영문)</label><input type="text" v-model="mForm.jobEnm" class="full" placeholder="예: Booking, B/L Issue">
            <label>전화번호</label><input type="text" v-model="mForm.tel">
            <label>이메일</label><input type="text" v-model="mForm.eml">
            <label>옵션</label>
            <span class="chk full"><input type="checkbox" id="nwPUseYn" v-model="mForm.useYnB"><label for="nwPUseYn">사용</label></span>
          </div>
        </div>
        <div class="nw-m-f">
          <button v-if="!mIsNew" type="button" class="nw-btn danger" :disabled="mSaving" @click="delM">🗑 삭제</button>
          <span class="sp"></span>
          <button type="button" class="nw-btn ghost" :disabled="mSaving" @click="closeM">취소</button>
          <button type="button" class="nw-btn save" :disabled="mSaving" @click="saveM">{{ mSaving ? '저장 중…' : '저장' }}</button>
        </div>
      </div>
    </div><!-- content_box // -->
  </div>
</template>

<script>
import { network } from '@/api/network-ctrList'
import networkApi from '@/api/services/network'
import apiCommons from '@/api/services/commons' // 포트코드(?portCd=HPH) → 포트명 조회용 (비로그인 허용 API)
import officeData from '@/assets/network/network-office-data.json' // API 실패 시 폴백용 정적 데이터
import ctrCodes from '@/assets/network/network-country-codes.json' // 국가코드/권역 (국가는 JSON 관리)
import { rootComputed } from '@/store/helpers'
import EBreadcrumbs from '@/components/common/EBreadcrumbs'
import NetworkMap from './NetworkMap'

export default {
  name: 'Network',
  components: {
    EBreadcrumbs,
    NetworkMap,
    rootComputed
  },
  data: function () {
    return {
      params: {
        ctrNm: '',
        ctrCd: ''
      },
      //국가 리스트
      ctrList: [],
      //연락처 정보 리스트
      ctrInfoKey: [],
      ctrInfoList: [],
      ctrFlag: '01',
      hoverCtr: '',
      hoverCtrs: [], // 권역 호버 시 소속국 seq 배열 (지도 일괄 하이라이트)
      officeMap: null, // DB(WB320I/321I/322I) API 응답을 국가코드별로 그룹핑 (미로딩/실패 시 정적 JSON 폴백)
      nwRole: { nwAdmin: 'N', nwUser: 'N' }, // CM112I 편집 권한 — 00526:전 국가 / 00527:자국만 (서버가 JWT ADMIN도 nwAdmin=Y로 승격)
      copiedKey: '', // 이메일 복사 피드백 표시 중인 행 key
      portFocusNm: '', // 쿼리 portCd로 조회한 포트명 — 사무소 카드 포커스 대기 중 (성공 시 비움)
      portFilterNm: '', // 포트 필터 표시용 라벨 (매칭 성공한 포트명)
      portFilterOfcCds: [], // 포트 필터: 매칭된 사무소 코드 목록 — 있으면 officeList가 이것만 표시
      dispLang: 'loc', // 상세 표기 언어 토글 — 로그인:LOCAL 고정 / 비로그인:KR(한국어) 접속만 LOCAL, 그 외 ENG (created에서 결정)
      langRight: 16, // 고정 토글의 오른쪽 오프셋 — 카드/지도 우측선에 정렬 (mounted에서 계산)
      // 관리자 편집 모달 상태
      mType: '', // '' | 'ofc' | 'area' | 'pic'
      mTitle: '',
      mPath: '',
      mIsNew: false,
      mSaving: false,
      mForm: {},
      mCtx: {},
      //홈페이지 여부 :: as-is 에서 title : 홈페이지일 경우 이미지로, e-svc일 경우 텍스트로 표시
      isHomePage: 'Y',
      showHKaddr: false
    }
  },
  computed: {
    ...rootComputed,
    // ctrFlag(seq) → 국가코드(keyNm) 매핑
    seqToCtr () {
      const m = {}
      const list = network.ctrInfoList || []
      list.forEach(e => { if (e.key && e.keyNm) m[e.key] = e.keyNm })
      return m
    },
    // 선택 국가의 사무소 목록 (office→area→pic) — API(officeMap) 우선, 실패 시 정적 JSON
    // 편집 권한자는 미사용(USE_YN='N') 포함 목록을 받으므로, 편집 불가 화면(공개 표)에서는 미사용을 걸러냄
    officeList () {
      const cc = this.seqToCtr[this.ctrFlag]
      const src = this.officeMap || officeData
      let list = (src[cc] && src[cc].offices) || []
      if (!this.isAdmin) {
        list = list
          .filter(o => o.useYn !== 'N')
          .map(o => Object.assign({}, o, {
            areas: (o.areas || [])
              .filter(a => a.useYn !== 'N')
              .map(a => Object.assign({}, a, { pics: (a.pics || []).filter(p => p.useYn !== 'N') }))
          }))
      }
      // 포트 포커스 필터 (?portCd 진입): 매칭된 사무소만 표시 — [전체 보기]로 해제
      if (this.portFilterOfcCds.length) {
        list = list.filter(o => this.portFilterOfcCds.indexOf(o.ofcCd) > -1)
      }
      return list
    },
    // 지도 도시 마커: DB(WB320I)에서 MAP_MARK_YN='Y' + 좌표 보유 사무소 → [lng, lat, 표시명, 대표여부]
    // 결과가 비면 NetworkMap이 내장 CFG 좌표로 폴백
    mapCities () {
      const list = []
      this.officeList.forEach(o => {
        const lat = parseFloat(o.lat); const lng = parseFloat(o.lng)
        if (o.mapMarkYn === 'Y' && o.useYn !== 'N' && isFinite(lat) && isFinite(lng)) { // 미사용 사무소는 지도 마커 제외
          list.push([lng, lat, o.area || o.enm || o.nm, list.length === 0, o.ofcCd]) // [4]=OFC_CD — 마커 클릭 시 카드 이동 키
        }
      })
      return list
    },
    // 권역 그룹 (network-country-codes.json region 필드, 파일 내 첫 등장 순서 유지)
    regionGroups () {
      const groups = []
      const idx = {}
      ctrCodes.forEach(c => {
        if (!(c.region in idx)) { idx[c.region] = groups.length; groups.push({ name: c.region, items: [] }) }
        groups[idx[c.region]].items.push({ seq: c.seq, name: c.name })
      })
      return groups
    },
    // 권역 행 배치: 국가가 적은 권역(East Africa+North America)은 한 줄에 함께 표시
    regionRows () {
      const SAME_ROW = { 'North America': 'East Africa' } // {뒤권역: 앞권역} — 앞권역 행에 이어 붙임
      const rows = []
      const rowIdx = {}
      this.regionGroups.forEach(rg => {
        const host = SAME_ROW[rg.name]
        if (host && host in rowIdx) { rows[rowIdx[host]].push(rg); return }
        rowIdx[rg.name] = rows.length
        rows.push([rg])
      })
      return rows
    },
    // JWT role=ADMIN (관리자 로그인, USER_TYPE 7) — 롤 API 응답 전에도 즉시 편집 UI 노출용
    jwtAdmin () {
      try {
        const payload = JSON.parse(atob(this.jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
        return payload.role === 'ADMIN'
      } catch (e) {
        return false
      }
    },
    // 현재 선택 국가의 편집 가능 여부 — 00526·JWT ADMIN=전 국가, 00527=계정 국가와 현재 국가가 같을 때만
    // (프론트는 UI 노출용, 최종 검증은 서버 canSave — 00527은 요청 항목의 소속 국가까지 역추적 확인)
    isAdmin () {
      if (this.jwtAdmin || this.nwRole.nwAdmin === 'Y') return true
      if (this.nwRole.nwUser === 'Y') {
        const cc = this.seqToCtr[this.ctrFlag]
        return !!cc && cc === (this.auth && this.auth.userCtrCd)
      }
      return false
    }
  },
  watch: {
    // Network 페이지가 이미 열린 상태에서 쿼리(ctrCd)만 바뀌면 created가 다시 안 타므로 국가 재선택
    '$route.query.ctrCd' (v) {
      if (v) this.init()
    },
    '$route.query.portCd' (v) {
      if (v) this.resolvePortFocus()
    }
  },
  created () {
    window.scrollTo(0, 0)
    // 표기 언어 기본값: 로그인=LOCAL 유지, 비로그인=KR(한국어 접속)만 LOCAL·그 외 ENG
    if (!(this.auth && this.auth.userId)) {
      const lang = this.serviceLang || (this.auth && this.auth.serviceLang) || 'KOR'
      this.dispLang = lang === 'KOR' ? 'loc' : 'eng'
    }
    this.init()
    this.loadOffices()
    this.loadRole()
    this.resolvePortFocus()
  },
  mounted () {
    // 고정 토글의 오른쪽 오프셋을 콘텐츠 박스 우측선에 맞춤 (리사이즈 시 재계산)
    this._onResizeLang = () => {
      const anchor = this.$refs.langAnchor
      if (!anchor) return
      const host = anchor.parentElement.getBoundingClientRect()
      this.langRight = Math.max(8, window.innerWidth - host.right)
    }
    this._onResizeLang()
    window.addEventListener('resize', this._onResizeLang)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this._onResizeLang)
  },
  methods: {
    // 권역 라벨 호버/클릭 (지도 일괄 하이라이트 / 권역 중심 이동)
    hoverRegion (rg) {
      this.hoverCtrs = rg ? rg.items.map(i => i.seq) : []
    },
    flyRegion (rg) {
      if (this.$refs.nwmap && this.$refs.nwmap.flyToRegion) this.$refs.nwmap.flyToRegion(rg.items.map(i => i.seq))
    },
    // 인셋 도시 마커 클릭 → 상세로 스크롤. DB 마커는 OFC_CD(키)로 사무소 카드 직행,
    // CFG 폴백 마커(ofcCd 없음)만 도시명↔AREA명 매칭 (마커명과 AREA명이 다를 수 있어 차선책)
    scrollToCity (city) {
      const ofcCd = (city && city.ofcCd) || ''
      const name = typeof city === 'string' ? city : (city && city.name) || ''
      let areaHit = null
      if (!ofcCd && name) {
        const norm = s => String(s || '').toLowerCase().replace(/[\s.-]/g, '')
        const target = norm(name)
        this.officeList.some(ofc => (ofc.areas || []).some(ar => {
          const en = norm(ar.enm); const lo = norm(ar.nm)
          if (en === target || lo === target || (en && (en.includes(target) || target.includes(en)))) { areaHit = ar; return true }
          return false
        }))
      }
      this.$nextTick(() => {
        let el = null
        if (ofcCd) el = document.querySelector('[data-ofc-cd="' + ofcCd + '"]')
        if (!el && areaHit) el = document.querySelector('[data-area-cd="' + areaHit.areaCd + '"]')
        const matched = !!el
        if (!el) el = document.querySelector('.nw-offices') // 매칭 실패 시 상세 목록 상단으로
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (matched) {
          // 사무소 카드는 자식 배경에 가려 카드 자체 플래시가 안 보임 → 헤더에 플래시
          const flashEl = el.querySelector('.nw-office-h') || el
          flashEl.classList.remove('nw-flash')
          flashEl.getBoundingClientRect() // 강제 리플로우 — 연속 클릭 시 애니메이션 재시작
          flashEl.classList.add('nw-flash')
          setTimeout(() => flashEl.classList.remove('nw-flash'), 1700)
        }
      })
    },
    // 쿼리 포트코드(?portCd=HPH, D/O 발행 & B/L 현황 팝업 등) → 포트명 조회 (HPH → HAIPHONG)
    // 비로그인 허용(Role.ANONYMOUS) API 사용 — 인증 필요 API를 쓰면 비로그인 401 → 메인으로 강제 이동됨
    // 조회 실패 시 조용히 국가 선택까지만 유지
    async resolvePortFocus () {
      const portCd = this.$route.query.portCd
      const ctrCd = this.$route.query.ctrCd
      if (!portCd || !ctrCd) return
      try {
        const res = await apiCommons.getCommonsPlacesInfo(String(ctrCd).toUpperCase(), String(portCd).toUpperCase())
        const info = res.data || {}
        this.portFocusNm = info.plcEnm || info.plcNm || ''
        this.tryFocusPort()
      } catch (e) {
        this.portFocusNm = ''
      }
    },
    // 포트명·사무소 목록·국가 선택이 모두 준비된 시점에 매칭 사무소만 필터로 남기고 첫 카드로 스크롤 (성공 시 1회만)
    // 사무소에 포트코드가 없어 이름 정규화 매칭 사용 — 매칭 실패 시 아무것도 안 함 (국가 선택까지만)
    tryFocusPort () {
      if (!this.portFocusNm || !this.officeList.length) return
      const norm = s => String(s || '').toLowerCase().replace(/[\s.-]/g, '')
      const target = norm(this.portFocusNm)
      if (!target) return
      const hits = []
      this.officeList.forEach(ofc => {
        const cand = [ofc.area, ofc.enm, ofc.nm]
        const areas = ofc.areas || []
        areas.forEach(ar => cand.push(ar.enm, ar.nm))
        const hit = cand.some(v => {
          const n = norm(v)
          return n && (n === target || n.includes(target) || target.includes(n))
        })
        if (hit) hits.push(ofc.ofcCd)
      })
      if (!hits.length) return
      this.portFilterNm = this.portFocusNm
      this.portFilterOfcCds = hits
      this.portFocusNm = ''
      this.scrollToCity({ ofcCd: hits[0] })
    },
    // 포트 필터 해제 → 해당 국가 전체 사무소 표시
    clearPortFilter () {
      this.portFilterNm = ''
      this.portFilterOfcCds = []
    },
    // ─── 관리자 편집 모달 (ADMIN JWT 전용) ───
    nwEsc (s) {
      return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
    },
    nwCtrLabel () {
      const cc = this.seqToCtr[this.ctrFlag]
      const it = (this.ctrList || []).find(x => x.seq === this.ctrFlag)
      return (it ? it.cdNm : '') + ' (' + cc + ')'
    },
    nwPath (parts, here) {
      const sep = '<span class="sep">›</span>'
      return parts.map(p => this.nwEsc(p)).join(sep) + sep + '<span class="here">' + this.nwEsc(here) + '</span>'
    },
    openOfc (ofc) {
      this.mType = 'ofc'; this.mIsNew = !ofc; this.mCtx = { ofc }
      this.mTitle = ofc ? '사무소 수정' : '사무소 추가'
      this.mPath = this.nwPath([this.nwCtrLabel()], ofc ? ofc.ofcCd + ' ' + (ofc.nm || ofc.enm) : '새 사무소')
      const o = ofc || {}
      this.mForm = {
        ofcCd: o.ofcCd || '',
        cat: o.type || 'KMTC',
        nm: o.nm || '',
        enm: o.enm || '',
        areaNm: o.area || '',
        tel: o.tel || '',
        addr: o.addr || '',
        addrEnm: o.addrEnm || '',
        lat: o.lat != null ? String(o.lat) : '',
        lng: o.lng != null ? String(o.lng) : '',
        mapUrl: o.mapUrl || '',
        mapYnB: o.mapMarkYn === 'Y',
        useYnB: o.useYn !== 'N' // 사용 체크박스 = USE_YN 저장 (해제 시 'N'으로 숨김, 조회 미노출)
      }
    },
    openArea (ofc, ar) {
      this.mType = 'area'; this.mIsNew = !ar; this.mCtx = { ofc, ar }
      this.mTitle = ar ? 'AREA 수정' : 'AREA 추가'
      this.mPath = this.nwPath([this.nwCtrLabel(), ofc.ofcCd + ' ' + (ofc.nm || ofc.enm)], ar ? ar.areaCd + ' ' + (ar.nm || ar.enm) : '새 AREA')
      const a = ar || {}
      this.mForm = { areaCd: a.areaCd || '', nm: a.nm || '', enm: a.enm || '', useYnB: a.useYn !== 'N' }
    },
    openPic (ofc, ar, pic) {
      this.mType = 'pic'; this.mIsNew = !pic; this.mCtx = { ofc, ar, pic }
      this.mTitle = pic ? '담당자 수정' : '담당자 추가'
      this.mPath = this.nwPath([this.nwCtrLabel(), ofc.ofcCd + ' ' + (ofc.nm || ofc.enm), ar.areaCd + ' ' + (ar.nm || ar.enm)], pic ? pic.picCd + ' ' + (pic.team || pic.teamEnm) : '새 담당자')
      const p = pic || {}
      this.mForm = { picCd: p.picCd || '', team: p.team || '', teamEnm: p.teamEnm || '', job: p.job || '', jobEnm: p.jobEnm || '', tel: p.tel || '', eml: p.email || '', useYnB: p.useYn !== 'N' }
    },
    closeM () {
      if (this.mSaving) return
      this.mType = ''; this.mForm = {}
    },
    async submitSave (payload) {
      this.mSaving = true
      try {
        await networkApi.saveNetworkAdmin(payload)
        await this.loadOffices() // 저장 후 재조회로 화면 갱신 (채번코드/삭제 반영)
        this.mSaving = false
        this.mType = ''; this.mForm = {}
      } catch (e) {
        this.mSaving = false
        alert('저장에 실패했습니다. 관리자 권한 또는 서버 상태를 확인하세요.')
      }
    },
    saveM () {
      const f = this.mForm
      if (this.mType === 'ofc') {
        if (!f.nm && !f.enm) return alert('사무소명(현지어 또는 영문)을 입력하세요')
        this.submitSave({ officeList: [{ state: this.mIsNew ? 'C' : 'U', ofcCd: this.mIsNew ? '' : f.ofcCd, ctrCd: this.seqToCtr[this.ctrFlag], cat: f.cat, nm: f.nm, enm: f.enm, areaNm: f.areaNm, addr: f.addr, addrEnm: f.addrEnm, tel: f.tel, lat: f.lat, lng: f.lng, mapUrl: f.mapUrl, mapYn: f.mapYnB ? 'Y' : 'N', useYn: f.useYnB ? 'Y' : 'N' }] })
      } else if (this.mType === 'area') {
        if (!f.nm && !f.enm) return alert('AREA명(현지어 또는 영문)을 입력하세요')
        this.submitSave({ areaList: [{ state: this.mIsNew ? 'C' : 'U', areaCd: this.mIsNew ? '' : f.areaCd, ofcCd: this.mCtx.ofc.ofcCd, nm: f.nm, enm: f.enm, useYn: f.useYnB ? 'Y' : 'N' }] })
      } else if (this.mType === 'pic') {
        if (!f.team && !f.teamEnm) return alert('팀명(현지어 또는 영문)을 입력하세요')
        this.submitSave({ picList: [{ state: this.mIsNew ? 'C' : 'U', picCd: this.mIsNew ? '' : f.picCd, areaCd: this.mCtx.ar.areaCd, team: f.team, teamEnm: f.teamEnm, job: f.job, jobEnm: f.jobEnm, tel: f.tel, eml: f.eml, useYn: f.useYnB ? 'Y' : 'N' }] })
      }
    },
    // ▲▼ 재정렬: 형제 목록에서 위치 이동 후 전체를 (순서×10)으로 재부여해 일괄 U 저장 (중복/구멍 자가치유)
    moveOfc (oi, dir) {
      const list = this.officeList.slice()
      const j = oi + dir
      if (j < 0 || j >= list.length) return
      const it = list.splice(oi, 1)[0]
      list.splice(j, 0, it)
      const cc = this.seqToCtr[this.ctrFlag]
      this.submitSave({ officeList: list.map((o, idx) => ({ state: 'U', ofcCd: o.ofcCd, ctrCd: cc, cat: o.type, nm: o.nm, enm: o.enm, areaNm: o.area, addr: o.addr, addrEnm: o.addrEnm, tel: o.tel, lat: o.lat != null ? String(o.lat) : '', lng: o.lng != null ? String(o.lng) : '', mapUrl: o.mapUrl || '', mapYn: o.mapMarkYn, useYn: o.useYn || 'Y', ord: (idx + 1) * 10 })) })
    },
    moveArea (ofc, ai, dir) {
      const list = (ofc.areas || []).slice()
      const j = ai + dir
      if (j < 0 || j >= list.length) return
      const it = list.splice(ai, 1)[0]
      list.splice(j, 0, it)
      this.submitSave({ areaList: list.map((a, idx) => ({ state: 'U', areaCd: a.areaCd, ofcCd: ofc.ofcCd, nm: a.nm, enm: a.enm, useYn: a.useYn || 'Y', ord: (idx + 1) * 10 })) })
    },
    movePic (ar, pi, dir) {
      const list = (ar.pics || []).slice()
      const j = pi + dir
      if (j < 0 || j >= list.length) return
      const it = list.splice(pi, 1)[0]
      list.splice(j, 0, it)
      this.submitSave({ picList: list.map((p, idx) => ({ state: 'U', picCd: p.picCd, areaCd: ar.areaCd, team: p.team, teamEnm: p.teamEnm, job: p.job, jobEnm: p.jobEnm, tel: p.tel, eml: p.email || '', useYn: p.useYn || 'Y', ord: (idx + 1) * 10 })) })
    },
    // 삭제 = DB 영구 삭제(하드). 잠시 숨기려면 삭제 대신 [사용] 체크 해제(USE_YN='N')
    delM () {
      const f = this.mForm
      if (this.mType === 'ofc') {
        if (!confirm('이 사무소를 영구 삭제할까요?\n하위 AREA/담당자 데이터도 함께 삭제되며 복구할 수 없습니다.\n(잠시 숨기려면 삭제 대신 [사용] 체크를 해제하세요)')) return
        this.submitSave({ officeList: [{ state: 'D', ofcCd: f.ofcCd }] })
      } else if (this.mType === 'area') {
        if (!confirm('이 AREA를 영구 삭제할까요?\n하위 담당자 데이터도 함께 삭제되며 복구할 수 없습니다.')) return
        this.submitSave({ areaList: [{ state: 'D', areaCd: f.areaCd }] })
      } else if (this.mType === 'pic') {
        if (!confirm('이 담당자를 영구 삭제할까요? 복구할 수 없습니다.')) return
        this.submitSave({ picList: [{ state: 'D', picCd: f.picCd }] })
      }
    },
    // AREA 클릭 → 지도: DB 지도URL(MAP_URL) 우선, 없으면 주소로 구글맵 검색, 그것도 없으면 좌표
    openMap (ofc) {
      let url = ''
      if (this.$ekmtcCommon.isNotEmpty(ofc.mapUrl)) {
        url = ofc.mapUrl
      } else if (this.$ekmtcCommon.isNotEmpty(ofc.addr) || this.$ekmtcCommon.isNotEmpty(ofc.addrEnm)) {
        url = 'https://www.google.com/maps?q=' + encodeURIComponent(ofc.addr || ofc.addrEnm)
      } else if (ofc.lat && ofc.lng) {
        url = 'https://www.google.com/maps?q=' + ofc.lat + ',' + ofc.lng
      } else {
        url = 'https://www.google.com/maps?q=' + encodeURIComponent((ofc.area || '') + ' ' + (ofc.enm || ofc.nm || ''))
      }
      window.open(url, '_blank')
    },
    // 이메일 클릭 → 클립보드 복사 (mailto 대신). clipboard API 미지원/비보안 컨텍스트는 textarea 폴백
    copyEmail (email, key) {
      const done = () => {
        this.copiedKey = key
        if (this._copiedTimer) clearTimeout(this._copiedTimer)
        this._copiedTimer = setTimeout(() => { this.copiedKey = '' }, 1500)
      }
      const fallback = () => {
        const ta = document.createElement('textarea')
        ta.value = email
        ta.style.position = 'fixed'; ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        try { document.execCommand('copy'); done() } catch (e) { /* 복사 실패 시 피드백 생략 */ }
        document.body.removeChild(ta)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(email).then(done).catch(fallback)
      } else {
        fallback()
      }
    },
    // CM112I 편집 권한 조회 (00526/00527) — 미로그인·권한없음·오류 시 일반 화면 유지
    async loadRole () {
      if (!this.auth || !this.auth.userId) return
      try {
        const res = await networkApi.getNetworkRole()
        const r = res.data || {}
        this.nwRole = { nwAdmin: r.nwAdmin === 'Y' ? 'Y' : 'N', nwUser: r.nwUser === 'Y' ? 'Y' : 'N' }
        // 편집 권한자는 미사용(USE_YN='N') 포함 목록으로 재조회 — 흐리게 표시 + 재활성화 가능
        if (this.nwRole.nwAdmin === 'Y' || this.nwRole.nwUser === 'Y') await this.loadOffices()
      } catch (e) {
        this.nwRole = { nwAdmin: 'N', nwUser: 'N' }
      }
    },
    // DB(WB320I/321I/322I) 사무소 데이터 로딩 — 전체 국가 1회 조회 후 국가코드별 그룹핑, 실패 시 정적 JSON 유지
    // 편집 권한 확인 후에는 미사용 포함 목록(관리자 API)으로 자동 전환
    async loadOffices () {
      try {
        const useAdm = this.nwRole.nwAdmin === 'Y' || this.nwRole.nwUser === 'Y'
        const res = useAdm ? await networkApi.getNetworkOfficesAdm() : await networkApi.getNetworkOffices()
        const offices = (res.data && res.data.offices) || []
        if (offices.length > 0) {
          const map = {}
          offices.forEach(o => {
            if (!map[o.ctrCd]) map[o.ctrCd] = { offices: [] }
            map[o.ctrCd].offices.push(o)
          })
          this.officeMap = map
        }
        this.tryFocusPort() // 포트 카드 포커스 대기 중이면 시도 (목록 준비 완료 시점)
      } catch (e) {
        // 폴백: officeMap이 null이면 officeList가 정적 JSON을 사용
      }
    },
    // 표기 언어: 페이지 내 토글(dispLang) 기준 — 엄격 모드(폴백 없음): 선택 언어의 값만 표시, 없으면 빈 값('-' 처리)
    locName (loc, eng) {
      return this.dispLang === 'loc' ? (loc || '') : (eng || '')
    },
    // '-'나 공백뿐인 값은 빈 값으로 취급 — DB에 placeholder('-')로 입력된 레거시 데이터 방어 (표시는 화면에서 '-' 가운데 정렬로 통일)
    cellVal (v) {
      const s = String(v == null ? '' : v).trim()
      return (s === '' || s === '-') ? '' : s
    },
    // office의 area/pic를 표 행으로 평탄화 (area는 첫 행에 rowspan, 표기 언어는 토글 기준)
    officeRows (ofc) {
      const rows = []
      const areas = ofc.areas || []
      areas.forEach(ar => {
        const pics = ar.pics || []
        pics.forEach((pic, i) => {
          rows.push({ first: i === 0, area: this.locName(ar.nm, ar.enm), areaCd: ar.areaCd, areaSpan: pics.length, key: pic.picCd, team: this.locName(pic.team, pic.teamEnm), job: this.cellVal(this.locName(pic.job, pic.jobEnm)), tel: this.cellVal(pic.tel), email: this.cellVal(pic.email) })
        })
      })
      return rows
    },
    textAreaHtcontrol (data) {
      let ht = ''

      switch (data) {
        case '2':
         ht = 'height50px'
         break
        case '3':
         ht = 'height70px'
         break
        case '4':
         ht = 'height90px'
         break
        case '5':
         ht = 'height110px'
         break
        case '8':
         ht = 'height170px'
         break
      }

      return ht
    },
    async init () {
      const TH = this
      let tmpCtrCd = ''
      let tmpKey = ''

      // 0) 쿼리로 국가코드 전달 시(D/O 발행 & B/L 현황 팝업 CONTACT 버튼 등) 해당 국가 우선 선택
      const qCtrCd = this.$route.query.ctrCd
      if (qCtrCd) {
        tmpCtrCd = String(qCtrCd).toUpperCase()
      } else if (this.auth.userId) {
        //현재 로그인한 유저의 국가코드를 가져오기
        const data = this.memberDetail
        tmpCtrCd = data.userCtrCd
      }

      //국가별 리스트
      TH.ctrList = await network.ctrList

      //ctrInfoList 가져오기
      TH.ctrInfoList = await network.ctrInfoList

      // 1) 로그인 사용자: 사용자 국가로 기본 선택 (keyNm = 국가코드 매칭)
      if (tmpCtrCd && TH.ctrInfoList && TH.ctrInfoList.length > 0) {
        for (let i = 0; i < TH.ctrInfoList.length; i++) {
          if (tmpCtrCd === TH.ctrInfoList[i].keyNm) {
            tmpKey = TH.ctrInfoList[i].key
            break
          }
        }
      }

      // 2) 미로그인(또는 매칭 실패): 서비스 언어 기준 — KR/EN→한국(01), JP→일본(02), CN→중국(03)
      if (!tmpKey) {
        const lang = this.serviceLang || this.auth.serviceLang || 'KOR'
        tmpKey = lang === 'JPN' ? '02' : lang === 'CHN' ? '03' : '01'
      }

      this.getNetworkInfo(tmpKey)
    },
    async getNetworkInfo (flag) {
      const TH = this

      //초기화
      TH.ctrInfoList = []

      //연락처 리스트
      let ctrInfoList = await network.ctrInfoList
      let length = ctrInfoList.length

      if (length > 0) {
        ctrInfoList.forEach(i => {
          if (i.key === flag) {
            TH.ctrInfoList = i.info
          }
        })
      }

      setTimeout(this.btnChgImg(flag), 1000)
      this.tryFocusPort() // 포트 카드 포커스 대기 중이면 시도 (국가 선택 완료 시점)
    },
    btnChgImg (flag) {
      // 다른 국가 선택 시 포트 필터 해제 (이전 국가의 사무소 코드로 목록이 비는 것 방지)
      if (flag !== this.ctrFlag) this.clearPortFilter()

      //class 바꾸기
      $('.list_area').removeClass('on')
      $('#' + flag).addClass('on')

      //img 바꾸기
      this.ctrFlag = flag
    }
  }
}
</script>

<style scoped>
.pre {
  font-family: 'Noto Sans KR', sans-serif !important;
    font-size: 14px;
    font-weight: 300;
    line-height: 1.5;
    color: #000;
}

textarea {
  /* min-height: auto !important; */
  overflow:hidden;
}

.height50px {
  min-height: 50px !important;
}

.height70px {
  min-height: 70px !important;
}

.height90px {
  min-height: 90px !important;
}

.height110px {
  min-height: 110px !important;
}

.height170px {
  min-height: 170px !important;
}

table > tr > td{
    word-break: break-all;
}

/* ===== 모던 리디자인: 국가 메뉴(칩) + 연락처 표 ===== */
.page_title {
  font-size: 26px; font-weight: 800; letter-spacing: -.01em; color: #0b1f3a;
}
/* 내용 컨테이너 프레임(테두리/배경/그림자) 제거 — 상단 줄 없애 깔끔하게 */
.content_box { border: 0 !important; box-shadow: none !important; background: transparent !important; padding: 0 !important; }

/* 국가 메뉴 → 칩(pill) */
.network_menu { border: 0 !important; background: none !important; padding: 0 !important; }
/* overflow:visible — 전역(content.css) overflow:hidden이 호버 시 떠오르는 칩 상단을 잘라내는 것 방지 */
.network_menu ul { display: flex !important; flex-wrap: wrap; gap: 8px; margin: 10px 0 4px; padding: 0; list-style: none; border: 0 !important; overflow: visible !important; }
.network_menu li.list_area {
  margin: 0 !important; padding: 0 !important; float: none !important; width: auto !important;
  border: 0 !important; background: none !important; list-style: none;
}
.network_menu li.list_area a {
  display: inline-block; padding: 5px 12px; border-radius: 999px;
  background: #eef3fa; color: #45566f; font-size: 12px; font-weight: 500; line-height: 1.2;
  text-decoration: none !important; border: 1px solid #e3eaf5;
  transition: background .15s, color .15s, box-shadow .15s, transform .1s;
}
.network_menu li.list_area a:hover { background: #dfeaf8; color: #16386a; border-color: #b9d0ee; transform: translateY(-2px); box-shadow: 0 7px 16px -7px rgba(22, 56, 106, .5); }
/* 권역 그룹 행 */
.rg-row { display: flex; align-items: flex-start; gap: 10px; }
.rg-row ul { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 !important; padding: 0; list-style: none; border: 0 !important; }
/* 권역 라벨: 네이비 배경 + 흰 글자 */
.rg-label {
  /* 가장 긴 라벨(Middle East & North Africa) 기준 고정폭 — min-width:auto가 내용폭으로 늘어나는 것 방지 */
  flex: 0 0 170px; min-width: 0; box-sizing: border-box; display: inline-flex; justify-content: center; text-align: right; white-space: nowrap;
  padding: 5px 10px; border-radius: 999px; background: #1f3a6e; color: #fff; border: 1px solid #1f3a6e;
  font-size: 11px; font-weight: 700; letter-spacing: .02em; text-decoration: none !important; user-select: none;
  transition: background .15s, transform .12s;
}
.rg-label:hover { background: #2c4f8f; color: #fff; transform: translateY(-1px); }
/* 같은 줄에 이어 붙는 두 번째 권역 라벨(East Africa 옆 North America) — 앞 권역 칩과 간격 확보 */
.rg-row ul + .rg-label { margin-left: 140px; }
/* 선택 칩: 파스텔 살구 배경 + 진주황 글자 — 쨍한 주황 대신 부드러운 톤 */
.network_menu li.list_area.on a {
  background: #ffd7b8; color: #9a430c; border-color: #f7bd92;
  box-shadow: 0 8px 16px -7px rgba(240, 150, 80, .45); transform: translateY(-1px);
}
/* 키보드 포커스 가시화 */
.network_menu li.list_area a:focus-visible, .rg-label:focus-visible,
.nw-area-link:focus-visible, .nw-ebtn:focus-visible, .nw-btn:focus-visible {
  outline: 2px solid #2e63a8; outline-offset: 2px;
}
/* 스크린리더 전용 텍스트 */
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
/* 연락처: office→area→pic JSON 기반 카드 */
.nw-offices { margin-top: 6px; }
.nw-office { margin-bottom: 10px; border: 1px solid #e3eaf5; border-radius: 12px; overflow: hidden; }
.nw-office-h { display: flex; align-items: center; gap: 8px; padding: 10px 15px; background: #f4f8fd; border-bottom: 1px solid #e6edf7; font-size: 15px; }
.nw-office-h b { color: #16386a; }
.nw-office-h .nw-en { color: #5e6e88; font-size: 12px; font-weight: 400; }
.nw-badge { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 999px; letter-spacing: .02em; }
.nw-badge.km { color: #e85d12; background: #ffe2d2; }
.nw-badge.ag { color: #16457e; background: #cfe0f7; }
.nw-badge.off { color: #6b7a90; background: #e8edf5; }
/* 미사용(USE_YN='N') 항목: 편집 화면에서 흐리게 — [사용] 체크 후 저장으로 재활성화 */
.nw-off { opacity: .55; }
.nw-office-addr { padding: 8px 15px; font-size: 12px; color: #5a6b82; background: #fafcff; border-bottom: 1px solid #eef2f8; white-space: pre-line; }
.nw-office .nw-pic { margin: 0 !important; }
/* 표 본문: 좌측 정렬(전역 .tbl_col의 center 덮어씀). 빈 값(-) 셀과 AREA 셀만 가운데 */
.nw-office .nw-pic td { text-align: left; padding-left: 10px; }
.nw-office .nw-pic td.nw-c { text-align: center; padding-left: 5px; }
.nw-office .nw-pic td.nw-area-c { text-align: center; padding-left: 5px; }
/* 공개 표: 전역 .tbl_col 14px → 권역 축소 비율에 맞춰 13px */
.nw-office .nw-pic, .nw-office .nw-pic th, .nw-office .nw-pic td { font-size: 13px; }
/* rowspan 셀이라 2행부터는 TEAM td가 :first-child가 되어 border-left가 사라짐 → AREA 셀 오른쪽에 선을 그려 전 행 연결 */
.nw-area-c { font-weight: 600; background: #fafcff; vertical-align: middle; border-right: 1px solid #e1e3eb; text-align: center; }
.nw-area-c:hover { background: #eef4fc; }
/* 상세 표기 언어 토글 (LOCAL/ENG 세그먼트) — 헤더(고정, ~63px) 바로 아래 우측에 상시 고정 */
.nw-lang-float { position: fixed; top: 72px; z-index: 30; }
/* 떠 있는 컨트롤이라 경계가 또렷해야 함 — 연한 테두리는 흰 배경에서 뭉개져 보임 */
.nw-lang-tg { display: inline-flex; border: 1px solid #8fa9cd; border-radius: 999px; overflow: hidden; background: #fff; box-shadow: 0 8px 20px -8px rgba(11, 31, 58, .45); }
.nw-lang-tg button { border: 0; background: none; padding: 4px 14px; font-size: 11px; font-weight: 700; letter-spacing: .04em; color: #5a6b82; cursor: pointer; }
.nw-lang-tg button.on { background: #1f3a6e; color: #fff; }
.nw-lang-tg button:not(.on):hover { background: #dfeaf8; color: #16386a; }
/* 이메일 복사 버튼 — 링크처럼 보이되 클릭 시 클립보드 복사 */
.nw-email-copy { position: relative; background: none; border: 0; padding: 0; font: inherit; cursor: pointer; color: #16457e; }
.nw-email-copy:hover { text-decoration: underline; }
/* 복사됨 배지: 인라인이면 긴 이메일 줄바꿈에 휘말려 깨짐 → 이메일 위에 떠 있는 말풍선(레이아웃 무영향) */
.nw-copied {
  position: absolute; left: 50%; top: -24px; transform: translateX(-50%);
  white-space: nowrap; pointer-events: none; z-index: 3;
  font-size: 10px; font-weight: 700; color: #1a7f4e; background: #d9f2e5;
  border-radius: 999px; padding: 3px 9px; box-shadow: 0 4px 10px -4px rgba(10, 40, 25, .35);
}
/* 지도 도시 마커 클릭으로 이동해 온 AREA 행 강조 플래시 */
.nw-flash { animation: nwAreaFlash 1.6s ease-out 1; }
@keyframes nwAreaFlash {
  0% { background: #ffd7b8; }
  100% { background: #fafcff; }
}
/* 지도 열기: 키보드 접근을 위해 td 클릭 대신 button — 링크처럼 보이게 리셋, 셀 전체 폭 + 가운데 정렬 */
.nw-area-link { display: inline-block; width: 100%; text-align: center; background: none; border: 0; padding: 0; font: inherit; font-weight: 600; cursor: pointer; color: #16457e; }
.nw-area-link:hover { text-decoration: underline; }
.nw-area-link .nw-pin { font-size: 11px; opacity: .55; }
.nw-area-link:hover .nw-pin { opacity: 1; }
.nw-empty { margin-top: 20px; padding: 28px; text-align: center; color: #5f7086; border: 1px dashed #d4def0; border-radius: 12px; }
/* 포트 포커스 필터 안내 바 (?portCd 진입 시) — 선택 칩과 같은 살구 톤 */
.nw-port-filter {
  margin-top: 14px; display: flex; align-items: center; gap: 10px;
  padding: 9px 14px; border: 1px solid #f7bd92; background: #fff3ea; border-radius: 10px;
  font-size: 13px; color: #9a430c;
}
.nw-port-filter b { color: #9a430c; }
.nw-port-filter button {
  margin-left: auto; border: 1px solid #f0a468; background: #fff; color: #b34e0e;
  border-radius: 999px; padding: 3px 12px; font-size: 12px; font-weight: 700; cursor: pointer;
}
.nw-port-filter button:hover { background: #ffe3d1; }
/* ─── 관리자 편집 (ADMIN JWT 전용) ─── */
.nw-adm-bar { margin: 18px 0 0; display: flex; gap: 8px; }
.nw-lvl { font-size: 10px; font-weight: 800; color: #fff; background: #7fa6d9; border-radius: 4px; padding: 2px 6px; letter-spacing: .04em; }
.nw-lvl.l1 { background: #2e63a8; }
.nw-lvl.l2 { background: #5b8ac2; }
.nw-lvl.l3 { background: #8fb2d9; }
.nw-h-btns { margin-left: auto; display: flex; gap: 6px; }
.nw-ebtn { display: inline-flex; align-items: center; gap: 4px; border-radius: 7px; padding: 4px 9px; font-size: 12px; font-weight: 700; cursor: pointer; background: #eef4fc; color: #2e63a8; border: 1px solid #cfdff5; }
.nw-ebtn:hover { background: #dfeaf8; }
.nw-ebtn.add { background: #2e63a8; color: #fff; border-color: #2e63a8; }
.nw-ebtn.big { padding: 8px 16px; font-size: 13px; }
/* ▲▼: 최소 터치 타깃 24×24px 확보 */
.nw-ebtn.ordb { padding: 4px 7px; font-size: 10px; min-width: 24px; min-height: 24px; justify-content: center; }
.nw-ebtn:disabled { opacity: .35; cursor: default; }
.nw-tree { padding: 6px 14px 12px; }
.nw-node-area { border-left: 3px solid #9cc0ee; margin: 12px 0 4px 10px; border-radius: 0 10px 10px 0; background: #fbfdff; overflow: hidden; }
.nw-node-h { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #f0f6fd; border-bottom: 1px solid #e6edf7; font-size: 12px; }
.nw-node-h b { color: #16457e; }
.nw-cd { font-family: Consolas, monospace; font-size: 11px; color: #556d94; white-space: nowrap; }
.nw-pt { width: 100%; border-collapse: collapse; font-size: 12px; }
.nw-pt th { background: #f7fafd; color: #5a7295; padding: 5px 8px; border-bottom: 1px solid #e8edf5; font-size: 11px; text-align: left; }
.nw-pt td { padding: 6px 8px; border-bottom: 1px solid #f0f4fa; text-align: left; }
.nw-pt tr:last-child td { border-bottom: 0; }
.nw-pt td.nw-r { text-align: right; }
.nw-nopic { padding: 10px 14px; color: #5f7086; font-size: 11px; }
.nw-addline { margin: 10px 0 4px 10px; }
/* 모달 */
.nw-mbg { position: fixed; inset: 0; background: rgba(10, 25, 50, .45); z-index: 50; }
.nw-modal { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); width: min(640px, 94vw); max-height: 88vh; background: #fff; border-radius: 14px; z-index: 51; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 30px 60px -20px rgba(8, 20, 40, .5); }
.nw-m-h { padding: 13px 18px; background: #12345f; color: #fff; font-weight: 700; display: flex; align-items: center; font-size: 14px; }
.nw-m-x { margin-left: auto; cursor: pointer; background: none; border: 0; color: #9db6d8; font-size: 16px; }
.nw-m-path { padding: 9px 18px; background: #eef4fc; border-bottom: 1px solid #dde7f5; font-size: 12px; color: #2c4a75; font-weight: 700; }
.nw-m-path >>> .sep { color: #9db6d8; margin: 0 6px; font-weight: 400; }
.nw-m-path >>> .here { color: #e85d12; }
.nw-m-b { padding: 16px 20px; overflow: auto; }
.nw-frm { display: grid; grid-template-columns: 96px 1fr 96px 1fr; gap: 10px 12px; align-items: center; }
.nw-frm label { font-weight: 700; color: #3a5578; text-align: right; font-size: 12px; }
/* 전역(base.css) input:focus + label { outline:1px dotted } — 체크박스용 규칙이 모든 input에 걸려,
   텍스트 입력 포커스 시 다음 항목의 라벨에 점선 상자가 생김 → 텍스트 입력만 해제 (체크박스 포커스 표시는 유지) */
.nw-frm input[type="text"]:focus + label { outline: none; }
/* 전역(base.css)이 input/select를 height:24px로 고정 — 폼 컨트롤 전체를 30px로 통일 (상하패딩 0 = 세로 중앙정렬) */
.nw-frm input[type="text"], .nw-frm select { width: 100%; height: 30px; border: 1px solid #c8d4e6; border-radius: 7px; padding: 0 9px; font-size: 13px; }
.nw-frm select { padding-right: 24px; letter-spacing: 0; }
.nw-frm .full { grid-column: 2 / 5; }
.nw-frm .chk { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #3a5578; }
/* 전역 커스텀 체크박스(라벨 :before 18px 박스 + padding-left 24px)는 박스-텍스트 간격이 6px뿐 → 패딩 확대 */
.nw-frm .chk label { font-weight: 600; text-align: left; margin-right: 14px; margin-left: 0; padding-left: 20px; }
.nw-frm .chk input { accent-color: #ff6a1a; width: 15px; height: 15px; }
.nw-m-f { padding: 12px 18px; border-top: 1px solid #e8edf5; display: flex; gap: 8px; align-items: center; }
.nw-m-f .sp { margin-left: auto; }
.nw-btn { border: 0; border-radius: 8px; padding: 9px 20px; font-size: 13px; font-weight: 700; cursor: pointer; }
.nw-btn.save { background: #ff6a1a; color: #fff; }
.nw-btn.ghost { background: #eef2f8; color: #4a6076; }
.nw-btn.danger { background: #fff; color: #c23b3b; border: 1px solid #eec4c4; }
.nw-btn:disabled { opacity: .55; cursor: default; }

/* 연락처 표 → 모던 카드형 */
.tbl_col.mt20 {
  width: 100%; border-collapse: separate !important; border-spacing: 0 !important;
  margin-top: 26px; border: 1px solid #e6edf6 !important; border-radius: 14px;
  overflow: hidden; box-shadow: 0 14px 34px -26px rgba(11, 31, 58, .5); background: #fff;
}
.tbl_col.mt20 thead th {
  background: #f3f8fd !important; color: #2f4d77 !important;
  font-size: 12px !important; font-weight: 700 !important; letter-spacing: .045em;
  text-transform: uppercase; text-align: left !important; white-space: nowrap;
  padding: 14px 18px !important; border: 0 !important; border-bottom: 1px solid #dbe6f3 !important;
}
.tbl_col.mt20 tbody td {
  padding: 13px 18px !important; border: 0 !important; border-bottom: 1px solid #eef3f9 !important;
  color: #3b4a5e !important; font-size: 13.5px !important; line-height: 1.45; vertical-align: middle;
  background: #fff;
}
.tbl_col.mt20 tbody td.border_left { border-left: 0 !important; }
.tbl_col.mt20 tbody td, .tbl_col.mt20 tbody td * { color: #3b4a5e !important; }
.tbl_col.mt20 tbody td[rowspan] { color: #0d2c57 !important; font-weight: 700; vertical-align: middle; }
.tbl_col.mt20 tbody tr:hover > td { background: #f7fbff; }
.tbl_col.mt20 tbody tr:last-child > td { border-bottom: 0 !important; }
.tbl_col.mt20 textarea {
  border: 0 !important; background: transparent; resize: none; width: 100%;
  color: #3b4a5e; font-size: 13.5px; line-height: 1.45; padding: 0; font-family: inherit;
}
</style>
